import React, { useState, useMemo, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useFormApi } from 'informed'
import { Row, Col } from 'react-flexbox-grid'

import { Checkbox, Text, Select, TextArea } from 'components/forms'
import { ConfirmModal } from 'components/Modal'
import Divider from 'components/Divider'
import { useQuery, useMutation } from 'components/graphql'
import getQueryName from 'helpers/getNameOfGqlQuery'
import getShopCRs from '../../gql/getShopCRs'
import { tseIdCR } from '../../gql/updateCashRegister'
import { StyledStatus, StyleParagraphStatus, TseNotificationsTitle, TseNotificationsText } from './styled'
import getDevice from './query/getDevice'


const Tse = ({ formData, data, appData }) => {
	const { id: cashRegisterId, hardwareId, shopId, tseRemoteCashRegisterId, deviceType } = data || {}
	const [tseLocation, setTseLocation] = useState(tseRemoteCashRegisterId)
	const { tseAdminPin, tseAdminPuk, tseTimeAdminPin, tseTimeAdminPuk } = formData?.parameters || {}
	const [editable, setEditable] = useState({
		tseAdminPin: !tseAdminPin,
		tseAdminPuk: !tseAdminPuk,
		tseTimeAdminPin: !tseTimeAdminPin,
		tseTimeAdminPuk: !tseTimeAdminPuk
	})
	const { data: deviceData } = useQuery(getDevice, { variables: { id: hardwareId }, skip: !hardwareId })
	const technicalIssuesContacts = appData?.technicalIssuesContacts
	// Stored as a raw comma/semicolon-separated string; normalise to ", " so the
	// list can wrap instead of overflowing as one long token.
	const formattedTechnicalIssuesContacts = (technicalIssuesContacts || '')
		.split(/[;,]/)
		.map(email => email.trim())
		.filter(Boolean)
		.join(', ')
	const activeTse = deviceData.getDevice?.isTSEStillLive
	const { data: shopCRsData, loading } = useQuery(getShopCRs, { variables: { id: shopId }, skip: !shopId })
	const [updateCR] = useMutation(tseIdCR)
	const locationOptions = useMemo(() => {
		const queryName = getQueryName(getShopCRs)
		return shopCRsData?.[queryName]?.cashRegisters || []
	}, [shopId, loading])
	const formApi = useFormApi()
	const [t] = useTranslation()
	const tseTypeForValidation = useRef()
	const isEpsonTse = typeof tseTypeForValidation.current === 'number'
		? tseTypeForValidation.current === 1
		: formApi?.getValues?.()?.parameters?.tseType === 1
	const isSwissbitType = typeof tseTypeForValidation.current === 'number'
		? tseTypeForValidation.current === 2
		: formApi?.getValues?.()?.parameters?.tseType === 2
	const isCryptasType = typeof tseTypeForValidation.current === 'number'
		? tseTypeForValidation.current === 3
		: formApi?.getValues?.()?.parameters?.tseType === 3
	const handleFocus = e => {
		const val = e?.target?.value
		const name = e?.target?.getAttribute?.('data-name')
		if (val && !editable?.[name]) {
			ConfirmModal({
				text: t('common.strings.are_you_sure')
			})
				.then(
					() => setEditable({ ...editable, [name]: true })
				)
		}
	}
	const setDefaults = typeTse => {
		let defaultPin
		let defaultPuk
		switch (typeTse) {
			// 'Cryptovision'
			case 0 :
				defaultPin = '11111111'
				defaultPuk = '1111111111'
				break
			// 'Epson' or 'Swissbit'
			case 1:
			case 2:
				defaultPin = '11111'
				defaultPuk = '111111'
				break
			// 'PrimeSign'
			default:
				defaultPin = '111111'
				defaultPuk = '1111111111'
				break
		}
		formApi.setValues({
			parameters: {
				tseAdminPin: defaultPin,
				tseAdminPuk: defaultPuk,
				tseTimeAdminPin: defaultPin,
				tseTimeAdminPuk: defaultPuk
			}
		})
		formApi.submitForm()
	}

	const handleActiveChange = e => {
		if (e.target.checked) {
			const parameters = formApi?.getValues?.()?.parameters || {}
			if (!parameters.tseAdminPin && !parameters.tseAdminPuk && !parameters.tseTimeAdminPin && !parameters.tseTimeAdminPuk) {
				setDefaults(1)
			}
		}
	}
	const handleLocationChange = val => {
		if (!isEpsonTse) {
			const id = val?.id
			setTseLocation(id)
			if (id) {
				updateCR({
					variables: {
						id: cashRegisterId,
						fields: {
							tseRemoteCashRegisterId: id
						}
					}
				})
			}
		}
	}
	const handleTypeChange = option => {
		tseTypeForValidation.current = option?.value
		setDefaults(option?.value)
	}

	const validateLength = (fieldType, fieldName) => val => {
		// 0 - 'Cryptovision', 1 - 'Epson', 2 - 'Swissbit', 3 - 'PrimeSign'
		const tseType = typeof tseTypeForValidation.current === 'number' ? tseTypeForValidation.current : formApi?.getValues?.()?.parameters?.tseType
		let minLength
		let maxLength
		switch (tseType) {
			// 'Cryptovision'
			case 0:
				if(fieldType === 'pin') {
					minLength = maxLength = 8
				}else{
					minLength = maxLength = 10
				}
				break
			// 'Epson' or 'Swissbit'
			case 1:
			case 2:
				if(fieldType === 'pin') {
					minLength = maxLength = 5
				}else{
					minLength = maxLength = 6
				}
				break
			// 'PrimeSign'
			default:
				minLength = 6
				maxLength = 12
				break
		}
		const codeIsTooSmall = val?.length < minLength
		const codeIsTooBig = val?.length > maxLength
		if (codeIsTooSmall) {
			return `${fieldName} ${t('validators.too_short', { value: minLength })}`
		}
		if(codeIsTooBig) {
			return `${fieldName} ${t('validators.too_long', { value: maxLength })}`
		}
	}

	return (
		<div>
			<Checkbox
				field='parameters.tseIsActive'
				label={t('common.fields.isActive')}
				onChange={handleActiveChange}
			/>
			<Row>
				<Col xs>
					<Text
						field='parameters.tseAdminPin'
						data-name='tseAdminPin'
						readOnly={!editable?.tseAdminPin}
						onClick={handleFocus}
						label={t('options_section.fields.admin_pin')}
						type='password'
						validate={validateLength('pin', t('options_section.fields.admin_pin'))}
						validateOnChange
						pwdVisibilityBtn
						disablePasswordAutoComplete
					/>
					{!isCryptasType && <Text
						field='parameters.tseAdminPuk'
						data-name='tseAdminPuk'
						readOnly={!editable?.tseAdminPuk}
						onClick={handleFocus}
						label={t('options_section.fields.admin_puk')}
						type='password'
						validate={validateLength('puk', t('options_section.fields.admin_pin'))}
						validateOnChange
						pwdVisibilityBtn
						disablePasswordAutoComplete
					/>}
					<Select
						field='parameters.tseType'
						label={t('options_section.fields.tse_type')}
						options={[
							{ value: 0, label: t('options_section.fields.tse_type_cryptovision') },
							{ value: 1, label: t('options_section.fields.tse_type_epson') },
							{ value: 2, label: t('options_section.fields.tse_type_swissbit') },
							{ value: 3, label: t('options_section.fields.tse_type_prime_sign') }
						]}
						onChange={handleTypeChange}
					/>
					<Divider />
					<StyleParagraphStatus color={activeTse ? 'greenColor' : 'errorColor'}>
						Status: <StyledStatus connect={activeTse}> {activeTse ? t('options_section.strings.status_tse_connect') : t('options_section.strings.status_tse_disconnect')}</StyledStatus>
					</StyleParagraphStatus>
					<Divider />
					<Checkbox
						field='parameters.tsePrintQRCode'
						label={t('options_section.fields.print_qr_code')}
					/>
				</Col>
				<Col xs>
					<Text
						field='parameters.tseTimeAdminPin'
						data-name='tseTimeAdminPin'
						readOnly={!editable?.tseTimeAdminPin}
						onClick={handleFocus}
						label={t('options_section.fields.time_admin_pin')}
						type='password'
						validate={validateLength('pin', t('options_section.fields.admin_pin'))}
						validateOnChange
						pwdVisibilityBtn
						disablePasswordAutoComplete
						hidden={isCryptasType}
					/>
					{!isSwissbitType && !isCryptasType && <Text
						field='parameters.tseTimeAdminPuk'
						data-name='tseTimeAdminPuk'
						readOnly={!editable?.tseTimeAdminPuk}
						onClick={handleFocus}
						label={t('options_section.fields.time_admin_puk')}
						validate={validateLength('puk', t('options_section.fields.admin_pin'))}
						type='password'
						validateOnChange
						pwdVisibilityBtn
						disablePasswordAutoComplete
						hidden={isEpsonTse}
					/>}
					{!isEpsonTse
						? !isCryptasType && <Select
							noForm
							label={t('options_section.fields.tse_location')}
							options={locationOptions}
							valueField='id'
							labelField='name'
							onChange={handleLocationChange}
							value={tseLocation}
						/>
						: !isSwissbitType && <Text
							field='parameters.tseEpsonIp'
							label={t('options_section.fields.tse_epson_ip')}
							keepState
						/>
					}
					<TextArea
						readOnly
						field='tseInfo.info'
						label={t('options_section.fields.tse_info')}
						style={{ height: '150px' }}
					/>
				</Col>
				{deviceType === 'SELF_CHECKOUT' && (
					<Col xs={5}>
						<TseNotificationsTitle>
							{t('options_section.strings.tse_notifications')}
						</TseNotificationsTitle>
						{formattedTechnicalIssuesContacts && (
							<TseNotificationsText>
								{t('options_section.strings.tse_notifications_sent_to')} <strong>{formattedTechnicalIssuesContacts}</strong>
							</TseNotificationsText>
						)}
						<TseNotificationsText>
							<Trans i18nKey='options_section.strings.tse_notifications_not_configured' />
						</TseNotificationsText>
					</Col>
				)}
			</Row>
		</div>
	)
}

export default Tse
